import React, { useContext, useEffect, useMemo, useState } from "react";
import DashboardLayout from "../../components/Layout/DashboardLayout";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import LoadingSpinner from "../../components/UI/LoadingSpinner";
import ScoreCircle from "../../components/ATS/ScoreCircle";
import KeywordBadges from "../../components/ATS/KeywordBadges";
import SuggestionCard from "../../components/ATS/SuggestionCard";
import AnalysisReport from "../../components/ATS/AnalysisReport";
import FileUploader from "../../components/ATS/FileUploader";
import * as resumeService from "../../services/resumeService";
import * as aiService from "../../services/aiService";
import { AuthContext } from "../../context/AuthContext";
import { toast } from "react-toastify";
import { exportToPDF } from "../../utils/exportPDF";

export default function ATSAnalyzer() {
  const { credits, updateCredits } = useContext(AuthContext);
  const [tab, setTab] = useState("existing"); // 'existing' | 'upload'
  const [resumes, setResumes] = useState([]);
  const [selectedResume, setSelectedResume] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await resumeService.getAllResumes(1, 100);
        if (mounted) setResumes(r.resumes || []);
        const h = await aiService.getAnalysisHistory(page, limit);
        if (mounted) {
          setHistory(h.analyses || []);
          setTotal(h.total || 0);
        }
      } catch {}
    })();
    return () => {
      mounted = false;
    };
  }, [page, limit]);

  const scoreColor = useMemo(() => {
    const s = analysisResult?.atsScore || 0;
    return s > 75
      ? "text-green-600"
      : s >= 50
      ? "text-yellow-600"
      : "text-red-600";
  }, [analysisResult]);

  const handleAnalyze = async () => {
    if (credits < 3) {
      toast.error("Insufficient credits (3 required)");
      return;
    }
    if (tab === "existing" && !selectedResume) {
      toast.error("Please select a resume");
      return;
    }
    if (tab === "upload" && !uploadedFile) {
      toast.error("Please upload a resume");
      return;
    }
    setIsAnalyzing(true);
    try {
      let result;
      if (tab === "existing") {
        result = await aiService.analyzeResume(selectedResume);
      } else {
        result = await aiService.analyzeUploadedResume(uploadedFile);
      }
      setAnalysisResult(result.analysis || result);
      await updateCredits();
      toast.success("Analysis complete!");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Analysis failed");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const downloadReport = async () => {
    const name = (
      resumes.find((r) => r._id === selectedResume)?.title || "ATS_Report"
    ).replace(/[^a-z0-9-_]+/gi, "_");
    await exportToPDF("ats-report", `${name}.pdf`);
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-bold">ATS Resume Analyzer</h1>
          <p className="text-gray-600">
            Get your resume scored and optimized for ATS systems
          </p>
        </header>

        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-2 border-b overflow-x-auto">
          <button
            className={`px-3 py-2 whitespace-nowrap ${
              tab === "existing"
                ? "border-b-2 border-primary-600 text-primary-700"
                : "text-gray-600"
            }`}
            onClick={() => setTab("existing")}
          >
            Analyze Existing
          </button>
          <button
            className={`px-3 py-2 whitespace-nowrap ${
              tab === "upload"
                ? "border-b-2 border-primary-600 text-primary-700"
                : "text-gray-600"
            }`}
            onClick={() => setTab("upload")}
          >
            Upload New
          </button>
          <span className="ml-auto text-xs text-gray-500 flex-shrink-0">
            Credits: {credits}
          </span>
        </div>

        {/* Analyze existing */}
        {tab === "existing" && (
          <Card title="Select Resume">
            <div className="flex flex-col gap-3">
              <select
                className="border rounded px-3 py-2 w-full"
                value={selectedResume}
                onChange={(e) => setSelectedResume(e.target.value)}
              >
                <option value="">Select a resume</option>
                {resumes.map((r) => (
                  <option key={r._id} value={r._id}>
                    {r.title}
                  </option>
                ))}
              </select>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleAnalyze}
                  disabled={isAnalyzing || credits < 3}
                  className="w-full sm:w-auto"
                >
                  {isAnalyzing ? "Analyzing..." : "Analyze Resume"}
                </Button>
                {credits < 3 && (
                  <span className="text-xs text-red-600 self-center">
                    Requires 3 credits
                  </span>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Upload new */}
        {tab === "upload" && (
          <Card title="Upload and Analyze">
            <FileUploader onFileSelect={setUploadedFile} />
            <div className="mt-3">
              <Button
                onClick={handleAnalyze}
                disabled={isAnalyzing || credits < 3}
              >
                {isAnalyzing ? "Analyzing..." : "Analyze"}
              </Button>
              {credits < 3 && (
                <span className="ml-3 text-xs text-red-600">
                  Requires 3 credits
                </span>
              )}
            </div>
          </Card>
        )}

        {/* Results */}
        {isAnalyzing && <LoadingSpinner />}
        {analysisResult && !isAnalyzing && (
          <Card title="Analysis Results">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="flex items-center justify-center md:col-span-2 lg:col-span-1">
                <ScoreCircle score={analysisResult.atsScore || 0} size={150} />
              </div>
              <div className="md:col-span-2 lg:col-span-2 space-y-3">
                <p className={`text-xl font-bold ${scoreColor}`}>
                  ATS Score: {analysisResult.atsScore || 0}
                </p>
                <p className="text-sm text-gray-600">
                  Readability: {analysisResult.readabilityScore || 0}
                </p>
                <div>
                  <p className="font-semibold text-sm">Keywords</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                    <div>
                      <p className="text-xs text-gray-500">Found</p>
                      <KeywordBadges
                        keywords={analysisResult.keywords?.found || []}
                        type="found"
                      />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Missing</p>
                      <KeywordBadges
                        keywords={analysisResult.keywords?.missing || []}
                        type="missing"
                      />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <p className="font-semibold text-sm">Strengths</p>
                    <ul className="list-disc ml-5 text-xs sm:text-sm">
                      {Array.isArray(analysisResult.strengths) ? (
                        (analysisResult.strengths || []).map((s, i) => (
                          <li key={i}>
                            {typeof s === "string" ? s : s?.title || ""}
                          </li>
                        ))
                      ) : (
                        <li className="text-gray-500">No data available</li>
                      )}
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-sm">Weaknesses</p>
                    <ul className="list-disc ml-5 text-xs sm:text-sm">
                      {Array.isArray(analysisResult.weaknesses) ? (
                        (analysisResult.weaknesses || []).map((s, i) => (
                          <li key={i}>
                            {typeof s === "string" ? s : s?.title || ""}
                          </li>
                        ))
                      ) : (
                        <li className="text-gray-500">No data available</li>
                      )}
                    </ul>
                  </div>
                </div>
                <div>
                  <p className="font-semibold text-sm">Suggestions</p>
                  <div className="space-y-2">
                    {Array.isArray(analysisResult.suggestions) ? (
                      (analysisResult.suggestions || []).map((s, i) => (
                        <SuggestionCard
                          key={i}
                          suggestion={typeof s === "string" ? { title: s } : s}
                          onApply={() => toast.info("Open editor and apply")}
                        />
                      ))
                    ) : (
                      <p className="text-sm text-gray-600">
                        No suggestions available
                      </p>
                    )}
                  </div>
                </div>
                <div className="pt-2">
                  <Button onClick={downloadReport}>
                    Download Report (PDF)
                  </Button>
                </div>
              </div>
            </div>
            {/* Hidden printable report */}
            <div className="hidden">
              <AnalysisReport
                analysisData={analysisResult}
                resumeName={
                  resumes.find((r) => r._id === selectedResume)?.title
                }
              />
            </div>
          </Card>
        )}

        {/* History */}
        <Card title="Previous Analyses">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2">Resume</th>
                  <th className="text-left px-4 py-2">Score</th>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">View</th>
                </tr>
              </thead>
              <tbody>
                {!history.length ? (
                  <tr>
                    <td
                      colSpan="4"
                      className="px-4 py-6 text-center text-gray-500"
                    >
                      No previous analyses.
                    </td>
                  </tr>
                ) : (
                  history.map((h) => (
                    <tr key={h._id} className="border-t">
                      <td className="px-4 py-2">{h.resumeTitle || "Resume"}</td>
                      <td className="px-4 py-2">{h.atsScore}</td>
                      <td className="px-4 py-2">
                        {new Date(h.analyzedAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-2">
                        <button
                          className="text-primary-600 underline"
                          onClick={() => setAnalysisResult(h)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-end gap-2 mt-3">
            <Button
              variant="ghost"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-500">
              Page {page} of {Math.max(1, Math.ceil(total / limit))}
            </span>
            <Button
              variant="ghost"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}
